import React, { useState, useEffect, useCallback } from 'react';
import util from '../../utils/util';
import {
    Card, Button, Select, Form, Input, Tag, Space, Badge,
    message, Spin, Progress, Row, Col, Statistic, Alert,
    Divider, Tooltip, InputNumber, DatePicker
} from 'antd';
import {
    SaveOutlined, SendOutlined, CheckCircleOutlined,
    ClockCircleOutlined, WarningOutlined, FileTextOutlined
} from '@ant-design/icons';
import MeService from '../../services/MeService';
import moment from 'moment';

const { Option }   = Select;
const { TextArea } = Input;

// ── Constants ─────────────────────────────────────────────────────────────
const PROGRESS_STATUSES = [
    { value:'not_started',   label:'Not Started',    color:'default' },
    { value:'in_progress',   label:'In Progress',    color:'processing' },
    { value:'partially_met', label:'Partially Met',  color:'warning' },
    { value:'met',           label:'Met',            color:'success' },
    { value:'not_met',       label:'Not Met',        color:'error' },
    { value:'completed',     label:'Completed',      color:'success' },
    { value:'not_applicable',label:'Not Applicable', color:'default' },
    { value:'delayed',       label:'Delayed',        color:'error' },
];

const CA_STATUSES = [
    { value:'none',        label:'None' },
    { value:'open',        label:'Open' },
    { value:'in_progress', label:'In Progress' },
    { value:'closed',      label:'Closed' },
];

const MEASUREMENT_LABELS = {
    yes_no:     'Yes / No',
    percentage: 'Percentage (%)',
    count:      'Count',
    score:      'Score',
    date:       'Date',
    text:       'Narrative',
    currency:   'Amount (E)',
};

const INDICATOR_TYPE_COLORS = {
    requirement: '#1890ff',
    output:      '#52c41a',
    outcome:     '#722ed1',
    process:     '#fa8c16',
    milestone:   '#13c2c2',
    kpi:         '#eb2f96',
};

// ── Indicator Progress Card ───────────────────────────────────────────────
function IndicatorCard({ indicator, progress, onSave, saving }) {
    const [form]     = Form.useForm();
    const [expanded, setExpanded] = useState(false);
    const [dirty,    setDirty]    = useState(false);

    useEffect(() => {
        if (progress) {
            form.setFieldsValue({
                ...progress,
                evidence_date:             progress.evidence_date ? moment(progress.evidence_date) : null,
                corrective_action_deadline: progress.corrective_action_deadline
                    ? moment(progress.corrective_action_deadline) : null,
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                status: 'not_started',
                corrective_action_status: 'none',
            });
        }
        setDirty(false);
    }, [progress, indicator.id]); // eslint-disable-line

    const handleValuesChange = () => setDirty(true);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            await onSave(indicator, {
                ...values,
                evidence_date: values.evidence_date
                    ? values.evidence_date.format('YYYY-MM-DD') : null,
                corrective_action_deadline: values.corrective_action_deadline
                    ? values.corrective_action_deadline.format('YYYY-MM-DD') : null,
            });
            setDirty(false);
            setExpanded(false);
        } catch(e) {
            if (e.errorFields) return; // validation error — don't close
            message.error(e.message || 'Save failed');
        }
    };

    const status     = form.getFieldValue('status') || progress?.status || 'not_started';
    const statusConf = PROGRESS_STATUSES.find(s => s.value === status) || PROGRESS_STATUSES[0];
    const typeColor  = INDICATOR_TYPE_COLORS[indicator.indicator_type] || '#1890ff';
    const isMet      = status === 'met' || status === 'completed';
    const isNA       = status === 'not_applicable';

    // Determine input type based on measurement_unit
    const renderValueInput = (name, label, placeholder) => {
        switch(indicator.measurement_unit) {
            case 'yes_no':
                return (
                    <Form.Item name={name} label={label}>
                        <Select placeholder={placeholder} allowClear>
                            <Option value="Yes">Yes</Option>
                            <Option value="No">No</Option>
                        </Select>
                    </Form.Item>
                );
            case 'percentage':
                return (
                    <Form.Item name={name} label={label}>
                        <InputNumber min={0} max={100} step={0.1} style={{ width:'100%' }}
                            addonAfter="%" placeholder={placeholder}/>
                    </Form.Item>
                );
            case 'count':
            case 'score':
            case 'currency':
                return (
                    <Form.Item name={name} label={label}>
                        <InputNumber style={{ width:'100%' }} placeholder={placeholder}
                            addonBefore={indicator.measurement_unit === 'currency' ? 'E' : null}/>
                    </Form.Item>
                );
            case 'date':
                return (
                    <Form.Item name={name} label={label}>
                        <DatePicker style={{ width:'100%' }} format="DD MMM YYYY"/>
                    </Form.Item>
                );
            default:
                return (
                    <Form.Item name={name} label={label}>
                        <Input placeholder={placeholder}/>
                    </Form.Item>
                );
        }
    };

    return (
        <Card
            size="small"
            style={{
                marginBottom:8,
                borderLeft:`4px solid ${isMet?'#52c41a':isNA?'#d9d9d9':typeColor}`,
                background: isMet ? '#f6ffed' : isNA ? '#fafafa' : '#fff',
            }}
            bodyStyle={{ padding:'10px 14px' }}
        >
            {/* Header row */}
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
                        {indicator.code && (
                            <span style={{ fontSize:11, fontWeight:800, color:'#888', flexShrink:0 }}>
                                {indicator.code}
                            </span>
                        )}
                        <Tag color={typeColor} style={{ fontSize:10, margin:0 }}>
                            {indicator.indicator_type}
                        </Tag>
                        {indicator.is_mandatory == 1 && (
                            <Tag color="red" style={{ fontSize:10, margin:0 }}>Required</Tag>
                        )}
                        {indicator.is_critical == 1 && (
                            <Tag color="volcano" style={{ fontSize:10, margin:0 }}>Critical</Tag>
                        )}
                    </div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#1f3864',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {indicator.title}
                    </div>
                    {indicator.description && !expanded && (
                        <div style={{ fontSize:11, color:'#888', marginTop:1,
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {indicator.description}
                        </div>
                    )}
                </div>

                {/* Status + actions */}
                <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
                    {progress?.achievement_pct != null && (
                        <div style={{ textAlign:'center', minWidth:50 }}>
                            <div style={{ fontSize:14, fontWeight:800,
                                color: progress.achievement_pct >= 75 ? '#52c41a' :
                                       progress.achievement_pct >= 50 ? '#1890ff' : '#f5222d' }}>
                                {parseFloat(progress.achievement_pct).toFixed(0)}%
                            </div>
                            <div style={{ fontSize:10, color:'#888' }}>achieved</div>
                        </div>
                    )}
                    <Badge status={statusConf.color} text={statusConf.label}
                        style={{ fontSize:11, whiteSpace:'nowrap' }}/>
                    {progress?.verification_status === 'verified' && (
                        <CheckCircleOutlined style={{ color:'#52c41a', fontSize:16 }}/>
                    )}
                    <Button size="small"
                        type={dirty ? 'primary' : 'default'}
                        style={dirty ? { background:'#fa8c16', borderColor:'#fa8c16' } : {}}
                        onClick={() => setExpanded(e => !e)}>
                        {expanded ? 'Collapse' : dirty ? '● Edit' : 'Log Progress'}
                    </Button>
                    {dirty && (
                        <Button size="small" type="primary" icon={<SaveOutlined/>}
                            loading={saving} onClick={handleSave}
                            style={{ background:'#52c41a', borderColor:'#52c41a' }}>
                            Save
                        </Button>
                    )}
                </div>
            </div>

            {/* Expanded form */}
            {expanded && (
                <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #f0f0f0' }}>
                    {indicator.description && (
                        <div style={{ fontSize:12, color:'#555', marginBottom:12,
                            padding:'8px 10px', background:'#f8faff', borderRadius:6,
                            borderLeft:'3px solid #1890ff' }}>
                            <strong>Requirement:</strong> {indicator.description}
                        </div>
                    )}

                    <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
                        <Row gutter={12}>
                            <Col span={8}>
                                <Form.Item name="status" label="Status" rules={[{ required:true }]}>
                                    <Select>
                                        {PROGRESS_STATUSES.map(s => (
                                            <Option key={s.value} value={s.value}>
                                                <Badge status={s.color} text={s.label}/>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                {renderValueInput('actual_value', 'Actual Value',
                                    `e.g. ${indicator.target_value || 'value achieved'}`)}
                            </Col>
                            <Col span={8}>
                                {renderValueInput('target_value', 'Period Target',
                                    indicator.target_value || 'expected this period')}
                            </Col>
                        </Row>

                        <Form.Item name="progress_narrative" label="Progress Narrative">
                            <TextArea rows={2} placeholder="Describe what was done this period..."/>
                        </Form.Item>

                        <Form.Item name="challenges" label="Challenges / Barriers">
                            <TextArea rows={2} placeholder="Any obstacles encountered?"/>
                        </Form.Item>

                        {/* Evidence */}
                        <Divider orientation="left" style={{ fontSize:12, color:'#888' }}>Evidence</Divider>
                        <Row gutter={12}>
                            <Col span={16}>
                                <Form.Item name="evidence_description" label="Evidence Description">
                                    <Input placeholder="What evidence exists? (document name, record, observation)"/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="evidence_date" label="Evidence Date">
                                    <DatePicker style={{ width:'100%' }} format="DD MMM YYYY"/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="evidence_reference" label="Reference / Document Number">
                            <Input placeholder="File name, folder path, register number..."/>
                        </Form.Item>

                        {/* Corrective action */}
                        <Divider orientation="left" style={{ fontSize:12, color:'#888' }}>Corrective Action</Divider>
                        <Form.Item name="gap_identified" label="Gap Identified">
                            <TextArea rows={2} placeholder="What gap was found?"/>
                        </Form.Item>
                        <Row gutter={12}>
                            <Col span={16}>
                                <Form.Item name="corrective_action" label="Corrective Action">
                                    <TextArea rows={2} placeholder="What action is being taken?"/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="corrective_action_status" label="Action Status">
                                    <Select>
                                        {CA_STATUSES.map(s => (
                                            <Option key={s.value} value={s.value}>{s.label}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={12}>
                            <Col span={12}>
                                <Form.Item name="corrective_action_owner" label="Action Owner">
                                    <Input placeholder="Person responsible"/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="corrective_action_deadline" label="Deadline">
                                    <DatePicker style={{ width:'100%' }} format="DD MMM YYYY"/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="lessons_learned" label="Lessons Learned">
                            <TextArea rows={2} placeholder="What lessons can be shared?"/>
                        </Form.Item>
                    </Form>

                    <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:8 }}>
                        <Button onClick={() => { setExpanded(false); setDirty(false); form.resetFields(); }}>
                            Cancel
                        </Button>
                        <Button type="primary" icon={<SaveOutlined/>}
                            loading={saving} onClick={handleSave}
                            style={{ background:'#1f3864', borderColor:'#1f3864' }}>
                            Save Progress
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function MeProgressCapture() {
    const [assignments,  setAssignments]  = useState([]);
    // Permission checks
    const canAdd    = util.canAdd('me_capture');
    const canEdit   = util.canEdit('me_capture');
    const canDelete = util.canDelete('me_capture');
    const canExport = util.canExport('me_capture');
    const [assignment,   setAssignment]   = useState(null);
    const [periods,      setPeriods]      = useState([]);
    const [period,       setPeriod]       = useState('');
    const [indicators,   setIndicators]   = useState([]);
    const [progressMap,  setProgressMap]  = useState({});
    const [loading,      setLoading]      = useState(false);
    const [saving,       setSaving]       = useState(false);
    const [filterSection,setFilterSection]= useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Load assignments and periods on mount
    useEffect(() => {
        Promise.all([
            MeService.getAssignments({ status:'in_progress,not_started,on_track,delayed' }),
            MeService.getMePeriods(),
        ]).then(([aRes, pRes]) => {
            setAssignments(aRes.data?.data || []);
            const ps = pRes.data?.data || [];
            setPeriods(ps);
            const cur = ps.find(p => p.is_current == 1);
            if (cur) setPeriod(cur.label);
        }).catch(() => message.error('Failed to load'));
    }, []);

    // Load indicators + progress when assignment or period changes
    const loadProgress = useCallback(async () => {
        if (!assignment || !period) return;
        setLoading(true);
        try {
            const [detailRes, progressRes] = await Promise.all([
                MeService.getAssignmentDetail(assignment.id),
                MeService.getProgress({
                    assignment_id:    assignment.id,
                    reporting_period: period,
                }),
            ]);
            const detail = detailRes.data?.data;
            setIndicators(detail?.indicators || []);

            // Build progress map keyed by indicator_id
            const pmap = {};
            (progressRes.data?.data || []).forEach(p => { pmap[p.indicator_id] = p; });
            setProgressMap(pmap);
        } catch(e) { message.error('Failed to load progress'); }
        finally { setLoading(false); }
    }, [assignment, period]);

    useEffect(() => { loadProgress(); }, [loadProgress]);

    const handleSave = async (indicator, values) => {
        setSaving(true);
        try {
            const existing = progressMap[indicator.id];
            const payload  = {
                ...values,
                id:               existing?.id || null,
                assignment_id:    assignment.id,
                indicator_id:     indicator.id,
                facility_id:      assignment.facility_id,
                framework_id:     assignment.framework_id,
                reporting_period: period,
            };
            const r = await MeService.saveProgress(payload);
            if (r.data?.code !== 200) throw new Error(r.data?.message);
            message.success(`Progress saved for "${indicator.title}"`);
            // Update local map
            setProgressMap(prev => ({
                ...prev,
                [indicator.id]: { ...prev[indicator.id], ...values, id: r.data?.data?.id || prev[indicator.id]?.id }
            }));
        } catch(e) { throw e; }
        finally { setSaving(false); }
    };

    // Stats
    const total    = indicators.length;
    const logged   = indicators.filter(i => progressMap[i.id]).length;
    const met      = indicators.filter(i => ['met','completed'].includes(progressMap[i.id]?.status)).length;
    const notMet   = indicators.filter(i => ['not_met','delayed'].includes(progressMap[i.id]?.status)).length;
    const pct      = total ? Math.round(logged / total * 100) : 0;

    // Unique sections for filter
    const sectionOptions = [...new Map(
        indicators.map(i => [i.section_id, { id: i.section_id, title: i.section_title, code: i.section_code }])
    ).values()];

    const filtered = indicators.filter(i => {
        if (filterSection && String(i.section_id) !== String(filterSection)) return false;
        if (filterStatus) {
            const p = progressMap[i.id];
            if (filterStatus === 'logged'   && !p) return false;
            if (filterStatus === 'unlogged' &&  p) return false;
            if (filterStatus === 'met'      && !['met','completed'].includes(p?.status)) return false;
            if (filterStatus === 'not_met'  && !['not_met','delayed'].includes(p?.status)) return false;
        }
        return true;
    });

    // Group filtered indicators by section
    const grouped = filtered.reduce((acc, ind) => {
        const key = ind.section_id || 'general';
        if (!acc[key]) acc[key] = { title: ind.section_title || 'General', code: ind.section_code, items: [] };
        acc[key].items.push(ind);
        return acc;
    }, {});

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin:0 }}>✏️ Log Progress</h4>
                    <small className="text-muted">
                        {assignment
                            ? `${assignment.facility_name} · ${assignment.framework_name}`
                            : 'Select an assignment to log progress'}
                    </small>
                </div>
                {assignment && period && (
                    <div className="my-auto ml-auto">
                        <Tag color="blue" style={{ fontSize:13 }}>📅 {period}</Tag>
                        <Tag color={pct===100?'green':'blue'} style={{ fontSize:13 }}>
                            {logged}/{total} logged
                        </Tag>
                    </div>
                )}
            </div>

            <div className="page-pad">
                {/* Assignment + period selector */}
                <Card size="small" style={{ marginBottom:16 }}>
                    <Row gutter={12} align="middle">
                        <Col xs={24} sm={10}>
                            <div style={{ fontSize:12, color:'#888', marginBottom:4 }}>Assignment</div>
                            <Select
                                showSearch optionFilterProp="children"
                                placeholder="Select your assignment..."
                                style={{ width:'100%' }}
                                value={assignment?.id}
                                onChange={id => {
                                    const a = assignments.find(a => a.id === id);
                                    setAssignment(a || null);
                                    setProgressMap({});
                                    setIndicators([]);
                                }}
                            >
                                {assignments.map(a => (
                                    <Option key={a.id} value={a.id}>
                                        <div style={{ fontWeight:600 }}>{a.framework_name}</div>
                                        <div style={{ fontSize:11, color:'#888' }}>
                                            {a.facility_name} · {a.region}
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={8}>
                            <div style={{ fontSize:12, color:'#888', marginBottom:4 }}>Reporting Period</div>
                            <Select
                                style={{ width:'100%' }}
                                value={period}
                                onChange={setPeriod}
                                placeholder="Select period"
                            >
                                {periods.map(p => (
                                    <Option key={p.label} value={p.label}>
                                        {p.is_current == 1 ? '★ ' : ''}{p.label}
                                        {p.is_open == 0 && <Tag color="orange" style={{ marginLeft:4, fontSize:10 }}>Closed</Tag>}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={6} style={{ display:'flex', alignItems:'flex-end' }}>
                            <Button type="primary" style={{ width:'100%', background:'#1f3864', borderColor:'#1f3864' }}
                                disabled={!assignment || !period} onClick={loadProgress}>
                                Load Indicators
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {/* Progress overview */}
                {assignment && indicators.length > 0 && (
                    <Card size="small" style={{ marginBottom:16 }}>
                        <Row gutter={16} align="middle">
                            <Col flex="auto">
                                <Progress percent={pct} strokeColor={pct===100?'#52c41a':'#1890ff'}
                                    format={p => <span style={{ fontWeight:800 }}>{p}% logged</span>}/>
                                <div style={{ fontSize:12, color:'#888', marginTop:4 }}>
                                    {logged} of {total} indicators have progress records for {period}
                                </div>
                            </Col>
                            <Col>
                                <Row gutter={16}>
                                    {[
                                        { label:'Total',   val:total,   color:'#1f3864' },
                                        { label:'Logged',  val:logged,  color:'#1890ff' },
                                        { label:'Met',     val:met,     color:'#52c41a' },
                                        { label:'Not Met', val:notMet,  color:'#f5222d' },
                                    ].map(k => (
                                        <Col key={k.label}>
                                            <Statistic title={k.label} value={k.val}
                                                valueStyle={{ color:k.color, fontSize:18, fontWeight:800 }}/>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </Row>
                    </Card>
                )}

                {/* Filters */}
                {indicators.length > 0 && (
                    <Card size="small" style={{ marginBottom:12 }}>
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                            <Select value={filterSection} onChange={setFilterSection}
                                style={{ width:220 }} allowClear placeholder="All sections">
                                {sectionOptions.map(s => (
                                    <Option key={s.id} value={s.id}>
                                        {s.code && <span style={{ fontWeight:700, marginRight:4 }}>{s.code}</span>}
                                        {s.title}
                                    </Option>
                                ))}
                            </Select>
                            <Select value={filterStatus} onChange={setFilterStatus}
                                style={{ width:160 }} allowClear placeholder="All statuses">
                                <Option value="logged">Logged</Option>
                                <Option value="unlogged">Not yet logged</Option>
                                <Option value="met">Met / Completed</Option>
                                <Option value="not_met">Not Met / Delayed</Option>
                            </Select>
                            <Tag style={{ marginLeft:'auto' }}>
                                Showing {filtered.length} of {total} indicators
                            </Tag>
                        </div>
                    </Card>
                )}

                {/* Loading */}
                {loading && (
                    <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div>
                )}

                {/* Empty state */}
                {!loading && !assignment && (
                    <Card style={{ textAlign:'center', padding:40 }}>
                        <div style={{ fontSize:40, marginBottom:12 }}>✏️</div>
                        <div style={{ fontSize:16, fontWeight:600, color:'#1f3864', marginBottom:4 }}>
                            Select an Assignment
                        </div>
                        <div style={{ color:'#888' }}>
                            Choose your assigned framework and reporting period to start logging progress.
                        </div>
                    </Card>
                )}

                {!loading && assignment && indicators.length === 0 && (
                    <Alert type="info" showIcon
                        message="No indicators found for this assignment."
                        description="The framework may not have any indicators set up yet. Contact your M&E administrator."
                    />
                )}

                {/* Indicators grouped by section */}
                {!loading && Object.entries(grouped).map(([sectionId, group]) => (
                    <div key={sectionId} style={{ marginBottom:20 }}>
                        {/* Section header */}
                        <div style={{
                            display:'flex', alignItems:'center', gap:8,
                            padding:'8px 14px', marginBottom:8,
                            background:'#1f3864', borderRadius:8,
                        }}>
                            {group.code && (
                                <span style={{ background:'rgba(255,255,255,0.2)', color:'#fff',
                                    padding:'1px 8px', borderRadius:4, fontSize:11, fontWeight:800 }}>
                                    {group.code}
                                </span>
                            )}
                            <span style={{ fontWeight:800, color:'#fff', fontSize:14 }}>{group.title}</span>
                            <span style={{ marginLeft:'auto', color:'rgba(255,255,255,0.7)', fontSize:12 }}>
                                {group.items.filter(i => progressMap[i.id]).length}/{group.items.length} logged
                            </span>
                        </div>

                        {/* Indicators */}
                        {group.items.map(ind => (
                            <IndicatorCard
                                key={ind.id}
                                indicator={ind}
                                progress={progressMap[ind.id] || null}
                                onSave={handleSave}
                                saving={saving}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}


